import React, { createContext, useContext, useState, useEffect } from 'react';
import * as tf from '@tensorflow/tfjs';

interface FoodItem {
  id: string;
  name: string;
  confidence: number;
  nutrition: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    sugar: number;
    sodium: number;
    vitamins: { [key: string]: number };
    minerals: { [key: string]: number };
  };
  allergens: string[];
  dietaryTags: string[];
  healthScore: number;
  ingredients?: string[];
  boundingBox?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

interface Recipe {
  id: string;
  name: string;
  ingredients: string[];
  instructions: string[];
  cookingTime: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  nutrition: FoodItem['nutrition'];
  image?: string;
  video3D?: string;
}

interface FoodAnalysisContextType {
  model: tf.LayersModel | null;
  isModelLoading: boolean;
  analyzedFoods: FoodItem[];
  currentRecipe: Recipe | null;
  analyzeImage: (imageData: ImageData | HTMLImageElement) => Promise<FoodItem[]>;
  generateRecipe: (ingredients: string[]) => Promise<Recipe>;
  calculateHealthScore: (nutrition: FoodItem['nutrition']) => number;
  clearAnalysis: () => void;
  setCurrentRecipe: (recipe: Recipe | null) => void;
}

const FoodAnalysisContext = createContext<FoodAnalysisContextType | undefined>(undefined);

export const FoodAnalysisProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [model, setModel] = useState<tf.LayersModel | null>(null);
  const [isModelLoading, setIsModelLoading] = useState(true);
  const [analyzedFoods, setAnalyzedFoods] = useState<FoodItem[]>([]);
  const [currentRecipe, setCurrentRecipe] = useState<Recipe | null>(null);

  useEffect(() => {
    loadModel();
  }, []);

  const loadModel = async () => {
    try {
      setIsModelLoading(true);
      // In a real implementation, you would load a pre-trained food recognition model
      // For now, we'll simulate model loading
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create a mock model structure
      const mockModel = {
        predict: (_input: tf.Tensor) => {
          // Mock prediction logic
          return tf.randomNormal([1, 1000]); // 1000 food classes
        }
      } as tf.LayersModel;
      
      setModel(mockModel);
    } catch (error) {
      console.error('Failed to load model:', error);
    } finally {
      setIsModelLoading(false);
    }
  };

  const analyzeImage = async (imageData: ImageData | HTMLImageElement): Promise<FoodItem[]> => {
    if (!model) {
      throw new Error('Model not loaded');
    }

    try {
      // Convert image to tensor
      const tensor = tf.browser.fromPixels(imageData)
        .resizeNearestNeighbor([224, 224])
        .expandDims(0)
        .div(255.0);

      // Mock food recognition results
      const mockResults: FoodItem[] = [
        {
          id: '1',
          name: 'Grilled Salmon',
          confidence: 0.95,
          nutrition: {
            calories: 206,
            protein: 22,
            carbs: 0,
            fat: 12,
            fiber: 0,
            sugar: 0,
            sodium: 59,
            vitamins: {
              'Vitamin D': 45,
              'Vitamin B12': 125,
              'Omega-3': 1800
            },
            minerals: {
              'Selenium': 40,
              'Phosphorus': 25
            }
          },
          allergens: ['Fish'],
          dietaryTags: ['High Protein', 'Keto-Friendly', 'Gluten-Free'],
          healthScore: 92,
          boundingBox: { x: 50, y: 50, width: 200, height: 150 }
        },
        {
          id: '2',
          name: 'Steamed Broccoli',
          confidence: 0.88,
          nutrition: {
            calories: 34,
            protein: 2.8,
            carbs: 7,
            fat: 0.4,
            fiber: 2.6,
            sugar: 1.5,
            sodium: 33,
            vitamins: {
              'Vitamin C': 135,
              'Vitamin K': 116,
              'Folate': 16
            },
            minerals: {
              'Iron': 4,
              'Calcium': 5
            }
          },
          allergens: [],
          dietaryTags: ['Vegan', 'Low Calorie', 'High Fiber'],
          healthScore: 96,
          boundingBox: { x: 300, y: 100, width: 150, height: 120 }
        }
      ];

      setAnalyzedFoods(mockResults);
      tensor.dispose();
      
      return mockResults;
    } catch (error) {
      console.error('Analysis failed:', error);
      throw error;
    }
  };

  const generateRecipe = async (ingredients: string[]): Promise<Recipe> => {
    // Mock recipe generation based on ingredients
    const mockRecipe: Recipe = {
      id: Date.now().toString(),
      name: `Delicious ${ingredients.join(' & ')} Bowl`,
      ingredients: [
        ...ingredients,
        'Olive oil',
        'Salt',
        'Black pepper',
        'Fresh herbs'
      ],
      instructions: [
        'Preheat your cooking surface to medium-high heat',
        'Prepare all ingredients by washing and chopping as needed',
        'Heat olive oil in a large pan',
        'Add main ingredients and cook according to their requirements',
        'Season with salt, pepper, and herbs',
        'Serve immediately while hot'
      ],
      cookingTime: 25,
      difficulty: 'Medium',
      nutrition: {
        calories: 350,
        protein: 25,
        carbs: 20,
        fat: 18,
        fiber: 8,
        sugar: 5,
        sodium: 450,
        vitamins: {
          'Vitamin A': 45,
          'Vitamin C': 80
        },
        minerals: {
          'Iron': 15,
          'Calcium': 12
        }
      }
    };

    setCurrentRecipe(mockRecipe);
    return mockRecipe;
  };

  const calculateHealthScore = (nutrition: FoodItem['nutrition']): number => {
    // Simple health score calculation based on nutritional values
    let score = 50; // Base score
    
    // Protein bonus
    if (nutrition.protein > 20) score += 20;
    else if (nutrition.protein > 10) score += 10;
    
    // Fiber bonus
    if (nutrition.fiber > 5) score += 15;
    else if (nutrition.fiber > 2) score += 8;
    
    // Low sodium bonus
    if (nutrition.sodium < 100) score += 10;
    else if (nutrition.sodium > 500) score -= 10;
    
    // Low sugar bonus
    if (nutrition.sugar < 5) score += 10;
    else if (nutrition.sugar > 15) score -= 10;
    
    // Vitamin/mineral bonus
    const vitaminCount = Object.keys(nutrition.vitamins || {}).length;
    const mineralCount = Object.keys(nutrition.minerals || {}).length;
    score += (vitaminCount + mineralCount) * 2;
    
    return Math.max(0, Math.min(100, score));
  };

  const clearAnalysis = () => {
    setAnalyzedFoods([]);
    setCurrentRecipe(null);
  };

  return (
    <FoodAnalysisContext.Provider value={{
      model,
      isModelLoading,
      analyzedFoods,
      currentRecipe,
      analyzeImage,
      generateRecipe,
      calculateHealthScore,
      clearAnalysis,
      setCurrentRecipe
    }}>
      {children}
    </FoodAnalysisContext.Provider>
  );
};

export const useFoodAnalysis = () => {
  const context = useContext(FoodAnalysisContext);
  if (context === undefined) {
    throw new Error('useFoodAnalysis must be used within a FoodAnalysisProvider');
  }
  return context;
};